import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaPhone,
  FaCalendarAlt,
  FaUser,
  FaTimes,
  FaSave,
  FaSync,
  FaSearch,
  FaStore,
  FaCheck,
  FaImage,
  FaPaintBrush,
  FaEraser,
  FaUndo,
  FaRedo,
  FaBold,
  FaItalic,
  FaListUl,
  FaMinus,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './Orders.css';

// Sanitize HTML - allow only safe tags
const sanitizeHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  // Remove script tags and event handlers
  div.querySelectorAll('script, style, iframe, object, embed').forEach(el => el.remove());
  div.querySelectorAll('*').forEach(el => {
    [...el.attributes].forEach(attr => {
      if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
    });
  });
  return div.innerHTML;
};

const ShopOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    phone_number: '',
    delivery_date: '',
    description: '',
    image_description: '',
    status: 'new',
    source: 'obchod' // Always set to 'obchod' for shop orders
  });
  
  // Image editor state
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]); // [{id, description}] when editing
  const [showCompleted, setShowCompleted] = useState(false); // completed orders collapsed by default
  const [currentEditingImage, setCurrentEditingImage] = useState(null);
  const [currentEditingIndex, setCurrentEditingIndex] = useState(null);
  // Image lightbox state
  // Lightbox: { orderId, ids: number[], index } or null. ids empty => use legacy single image.
  const [lightbox, setLightbox] = useState(null);
  // Cache of loaded thumbnails: { orderId: dataUri }
  const [thumbnailCache, setThumbnailCache] = useState({});
  const descriptionRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingTool, setDrawingTool] = useState('pen');
  const [drawingColor, setDrawingColor] = useState('#FF0000');
  const [lineWidth, setLineWidth] = useState(2);
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);

  useEffect(() => {
    fetchShopOrders();
  }, []);

  // Sync contentEditable with formData when form opens/changes
  useEffect(() => {
    if ((showAddForm || editingOrder) && descriptionRef.current) {
      // Only set innerHTML if it differs (avoid cursor jump)
      if (descriptionRef.current.innerHTML !== formData.description) {
        descriptionRef.current.innerHTML = formData.description || '';
      }
    }
  }, [showAddForm, editingOrder]);

  // Load image when editor modal opens
  useEffect(() => {
    if (showImageEditor && currentEditingImage) {
      loadImageToCanvas(currentEditingImage);
    }
  }, [showImageEditor]);

  const fetchShopOrders = async () => {
    try {
      // Pass source=obchod to get all shop orders (not just user's own)
      const response = await api.get('/orders?source=obchod');
      setOrders(response.data);
    } catch (error) {
      toast.error('Chyba pri načítaní objednávok z obchodu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double-submission
    if (submitting) return;

    if (!formData.customer_name) {
      toast.error('Meno zákazníka je povinné');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/orders', { ...formData, source: 'obchod' });

      // Upload images in parallel
      if (uploadedImages.length > 0 && response.data.id) {
        await Promise.all(
          uploadedImages.map(image =>
            api.post(`/orders/${response.data.id}/image`, { image })
          )
        );
      }

      toast.success('Objednávka z obchodu vytvorená');
      setFormData({
        customer_name: '',
        phone_number: '',
        delivery_date: '',
        description: '',
        image_description: '',
        status: 'new',
        source: 'obchod'
      });
      setUploadedImages([]);
      setExistingImages([]);
      setShowAddForm(false);
      fetchShopOrders();
    } catch (error) {
      toast.error('Chyba pri vytváraní objednávky');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (orderId) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.put(`/orders/${orderId}`, { ...formData, source: 'obchod' });

      // Upload new images in parallel
      if (uploadedImages.length > 0) {
        await Promise.all(
          uploadedImages.map(image =>
            api.post(`/orders/${orderId}/image`, { image })
          )
        );
      }

      toast.success('Objednávka aktualizovaná');
      setEditingOrder(null);
      setUploadedImages([]);
      setExistingImages([]);
      fetchShopOrders();
    } catch (error) {
      toast.error('Chyba pri aktualizácii objednávky');
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit form for an order; loads existing images as a gallery (not for re-upload)
  const startEdit = async (order) => {
    setEditingOrder(order.id);
    setFormData({
      customer_name: order.customer_name,
      phone_number: order.phone_number || '',
      delivery_date: order.delivery_date || '',
      description: order.description || '',
      image_description: order.image_description || '',
      status: order.status,
      source: 'obchod'
    });
    setUploadedImages([]); // only NEW images go here
    // Load existing images list (gallery)
    try {
      const resp = await api.get(`/orders/${order.id}/images`);
      setExistingImages(resp.data || []);
    } catch (e) {
      setExistingImages([]);
    }
    setShowAddForm(true);
  };

  // Delete an existing (already saved) image from an order being edited
  const handleDeleteExistingImage = async (imageId) => {
    if (!editingOrder) return;
    try {
      await api.delete(`/orders/${editingOrder}/images/${imageId}`);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
      // Invalidate thumbnail cache for this order
      setThumbnailCache(prev => {
        const next = { ...prev };
        delete next[editingOrder];
        return next;
      });
      toast.success('Obrázok odstránený');
    } catch (e) {
      toast.error('Chyba pri mazaní obrázka');
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Naozaj chcete zmazať túto objednávku?')) {
      return;
    }

    try {
      await api.delete(`/orders/${orderId}`);
      toast.success('Objednávka zmazaná');
      fetchShopOrders();
    } catch (error) {
      toast.error('Chyba pri mazaní objednávky');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}`, { status: newStatus });
      toast.success('Stav objednávky aktualizovaný');
      fetchShopOrders();
      // Trigger refresh of menu counter
      window.dispatchEvent(new Event('shopOrdersUpdated'));
    } catch (error) {
      toast.error('Chyba pri zmene stavu');
    }
  };

  // Image upload handler - supports multiple images
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validImages = files.filter(file => file.type.startsWith('image/'));
    
    if (validImages.length === 0) {
      toast.error('Prosím vyberte platné obrázky');
      return;
    }
    
    validImages.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImages(prev => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Load image to canvas
  const loadImageToCanvas = (imageSrc) => {
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error('Canvas not found');
        return;
      }
      
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const maxWidth = 800;
        const maxHeight = 600;
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        saveCanvasState();
      };
      
      img.onerror = () => {
        console.error('Failed to load image');
        toast.error('Chyba pri načítaní obrázka');
      };
      
      img.src = imageSrc;
    }, 100);
  };

  // Drawing functions
  const startDrawing = (e) => {
    if (!showImageEditor) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    
    if (drawingTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = lineWidth * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = drawingColor;
      ctx.lineWidth = lineWidth;
    }
    
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !showImageEditor) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      saveCanvasState();
    }
  };

  // Save canvas state for undo/redo
  const saveCanvasState = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const canvasData = canvas.toDataURL();
    const history = drawingHistory.slice(0, historyStep + 1);
    history.push(canvasData);
    setDrawingHistory(history);
    setHistoryStep(history.length - 1);
  };

  // Undo/Redo functions
  const handleUndo = () => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      loadImageToCanvas(drawingHistory[newStep]);
    }
  };

  const handleRedo = () => {
    if (historyStep < drawingHistory.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      loadImageToCanvas(drawingHistory[newStep]);
    }
  };

  // Save edited image
  const saveEditedImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const editedImage = canvas.toDataURL('image/png');
    if (currentEditingIndex !== null) {
      const updated = [...uploadedImages];
      updated[currentEditingIndex] = editedImage;
      setUploadedImages(updated);
    } else {
      setUploadedImages(prev => [...prev, editedImage]);
    }
    setShowImageEditor(false);
    setCurrentEditingImage(null);
    setCurrentEditingIndex(null);
    toast.success('Obrázok uložený');
  };

  // Component to load and display order image in detail (small thumbnail, click to zoom)
  // Renders one gallery image thumbnail (by order + image id), cached
  const GalleryThumb = ({ orderId, imageId, onClick }) => {
    const cacheKey = `${orderId}:${imageId}`;
    const [src, setSrc] = useState(thumbnailCache[cacheKey] || null);
    const [loading, setLoading] = useState(!thumbnailCache[cacheKey]);

    useEffect(() => {
      if (thumbnailCache[cacheKey]) return;
      let cancelled = false;
      (async () => {
        try {
          const resp = await api.get(`/orders/${orderId}/images/${imageId}/thumbnail`);
          if (cancelled) return;
          if (resp.data?.image) {
            setSrc(resp.data.image);
            setThumbnailCache(prev => ({ ...prev, [cacheKey]: resp.data.image }));
          }
        } catch (e) {
          console.error('Thumbnail load error:', e);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }, [orderId, imageId, cacheKey]);

    if (loading) return <div className="order-thumbnail-loading">Načítavam...</div>;
    if (!src) return null;

    return (
      <div className="order-thumbnail" onClick={onClick}>
        <img src={src} alt="Náhľad objednávky" loading="lazy" />
        <div className="order-thumbnail-overlay"><FaImage /> Zväčšiť</div>
      </div>
    );
  };

  // Renders an order's whole image gallery (uses image_ids from order data, or fetches)
  const OrderGallery = ({ orderId, imageIds }) => {
    const [ids, setIds] = useState(imageIds && imageIds.length ? imageIds : null);

    useEffect(() => {
      if (imageIds && imageIds.length) { setIds(imageIds); return; }
      let cancelled = false;
      (async () => {
        try {
          const resp = await api.get(`/orders/${orderId}/images`);
          if (!cancelled) setIds((resp.data || []).map(i => i.id));
        } catch (e) {
          if (!cancelled) setIds([]);
        }
      })();
      return () => { cancelled = true; };
    }, [orderId, imageIds]);

    if (ids === null) return <div className="order-thumbnail-loading">Načítavam náhľad...</div>;
    if (!ids.length) return null;

    return (
      <div className="order-gallery">
        {ids.map((imgId, idx) => (
          <GalleryThumb
            key={imgId}
            orderId={orderId}
            imageId={imgId}
            onClick={(e) => {
              e.stopPropagation();
              setLightbox({ orderId, ids, index: idx });
            }}
          />
        ))}
      </div>
    );
  };

  // Full-screen lightbox with prev/next navigation across an order's images
  const Lightbox = ({ orderId, ids, index, onClose, onNav }) => {
    const [src, setSrc] = useState('loading');
    const imageId = ids[index];

    useEffect(() => {
      let cancelled = false;
      setSrc('loading');
      (async () => {
        try {
          const resp = await api.get(`/orders/${orderId}/images/${imageId}`);
          if (cancelled) return;
          const img = resp.data?.image;
          setSrc(img ? (img.startsWith('data:') ? img : `data:image/png;base64,${img}`) : null);
        } catch (e) {
          if (!cancelled) setSrc(null);
        }
      })();
      return () => { cancelled = true; };
    }, [orderId, imageId]);

    return (
      <div className="image-lightbox" onClick={onClose}>
        <button className="lightbox-close" onClick={onClose}><FaTimes /></button>
        {ids.length > 1 && (
          <>
            <button className="lightbox-nav prev" onClick={(e) => { e.stopPropagation(); onNav(-1); }}>
              <FaChevronLeft />
            </button>
            <button className="lightbox-nav next" onClick={(e) => { e.stopPropagation(); onNav(1); }}>
              <FaChevronRight />
            </button>
            <div className="lightbox-counter">{index + 1} / {ids.length}</div>
          </>
        )}
        {src === 'loading' ? (
          <div className="lightbox-loading"><FaSync className="spinning" /> Načítavam obrázok...</div>
        ) : src ? (
          <img src={src} alt="Zväčšený náhľad" onClick={(e) => e.stopPropagation()} />
        ) : (
          <div className="lightbox-loading">Obrázok sa nepodarilo načítať</div>
        )}
      </div>
    );
  };

  // Filter orders by search term
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.phone_number?.includes(searchTerm) ||
                          order.order_number.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Separate active and completed orders
  const activeOrders = filteredOrders.filter(order => 
    order.status === 'new' || order.status === 'in_progress'
  ).sort((a, b) => {
    // Sort: new orders first, then in_progress
    if (a.status === 'new' && b.status !== 'new') return -1;
    if (a.status !== 'new' && b.status === 'new') return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });
  
  const completedOrders = filteredOrders.filter(order => 
    order.status === 'completed' || order.status === 'cancelled'
  ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return '#3b82f6';
      case 'in_progress': return '#f59e0b';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'new': return 'Nová';
      case 'in_progress': return 'Rozpracovaná';
      case 'completed': return 'Dokončená';
      case 'cancelled': return 'Zrušená';
      default: return status;
    }
  };

  // Execute formatting command on contentEditable
  const execFormat = (command, value = null) => {
    descriptionRef.current?.focus();
    document.execCommand(command, false, value);
  };

  // Sync contentEditable content to formData
  const handleDescriptionInput = () => {
    const el = descriptionRef.current;
    if (el) {
      setFormData(prev => ({ ...prev, description: el.innerHTML }));
    }
  };

  return (
    <div className="orders-page">
      <div className="page-header">
        <h1><FaStore /> Obchod - Objednávky</h1>
        <motion.button
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaPlus /> Nová objednávka
        </motion.button>
      </div>

      {/* Search Bar */}
      <div className="search-filter-bar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Hľadať podľa mena, telefónu alebo čísla..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {(showAddForm || editingOrder) && (
          <motion.div
            className="order-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <h2>{editingOrder ? 'Upraviť objednávku' : 'Nová objednávka z obchodu'}</h2>
            <form onSubmit={editingOrder ? (e) => { e.preventDefault(); handleUpdate(editingOrder); } : handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label><FaUser /> Meno zákazníka *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label><FaPhone /> Telefón</label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label><FaCalendarAlt /> Dátum dodania</label>
                  <input
                    type="text"
                    placeholder="Dátum alebo text (napr. 'čo najskôr')"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Stav</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="new">Nová</option>
                    <option value="in_progress">Rozpracovaná</option>
                    <option value="completed">Dokončená</option>
                    <option value="cancelled">Zrušená</option>
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Popis objednávky</label>
                <div className="description-editor">
                  <div className="description-toolbar">
                    <button type="button" className="fmt-btn" onMouseDown={(e) => { e.preventDefault(); execFormat('bold'); }} title="Tučné (Ctrl+B)">
                      <FaBold />
                    </button>
                    <button type="button" className="fmt-btn" onMouseDown={(e) => { e.preventDefault(); execFormat('italic'); }} title="Kurzíva (Ctrl+I)">
                      <FaItalic />
                    </button>
                    <button type="button" className="fmt-btn" onMouseDown={(e) => { e.preventDefault(); execFormat('insertUnorderedList'); }} title="Zoznam">
                      <FaListUl />
                    </button>
                    <button type="button" className="fmt-btn" onMouseDown={(e) => { e.preventDefault(); execFormat('insertHorizontalRule'); }} title="Oddeľovač">
                      <FaMinus />
                    </button>
                  </div>
                  <div
                    ref={descriptionRef}
                    className="description-editable"
                    contentEditable
                    onInput={handleDescriptionInput}
                    data-placeholder="Podrobný popis objednávky..."
                    suppressContentEditableWarning
                  />
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="image-section">
                <label><FaImage /> Obrázky</label>

                {/* Existing (already saved) images — only when editing */}
                {editingOrder && existingImages.length > 0 && (
                  <div className="existing-images-block">
                    <div className="existing-images-label">Priložené obrázky ({existingImages.length})</div>
                    <div className="images-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: '10px',
                      marginBottom: '15px'
                    }}>
                      {existingImages.map((img, idx) => (
                        <div key={img.id} style={{ position: 'relative' }}>
                          <div
                            className="existing-thumb"
                            onClick={() => setLightbox({
                              orderId: editingOrder,
                              ids: existingImages.map(i => i.id),
                              index: idx
                            })}
                          >
                            <GalleryThumb
                              orderId={editingOrder}
                              imageId={img.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLightbox({
                                  orderId: editingOrder,
                                  ids: existingImages.map(i => i.id),
                                  index: idx
                                });
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="thumb-delete-btn"
                            title="Odstrániť obrázok"
                            onClick={() => handleDeleteExistingImage(img.id)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="image-controls">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    id="image-upload"
                    hidden
                  />
                  <label htmlFor="image-upload" className="btn btn-secondary">
                    <FaImage /> Nahrať obrázky
                  </label>
                </div>
                
                {uploadedImages.length > 0 && editingOrder && (
                  <div className="existing-images-label" style={{ marginTop: '10px' }}>
                    Nové obrázky ({uploadedImages.length})
                  </div>
                )}
                {uploadedImages.length > 0 && (
                  <div className="images-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: '10px',
                    marginTop: '15px'
                  }}>
                    {uploadedImages.map((image, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img 
                          src={image} 
                          alt={`Preview ${index + 1}`}
                          style={{ 
                            width: '100%', 
                            height: '150px', 
                            objectFit: 'cover',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: '2px solid rgba(255, 255, 255, 0.1)'
                          }}
                          onClick={() => {
                            setCurrentEditingImage(image);
                            setCurrentEditingIndex(index);
                            setShowImageEditor(true);
                          }}
                        />
                        <button
                          type="button"
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            background: 'rgba(239, 68, 68, 0.9)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '25px',
                            height: '25px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onClick={() => {
                            const updated = uploadedImages.filter((_, i) => i !== index);
                            setUploadedImages(updated);
                          }}
                        >
                          ×
                        </button>
                        <div style={{
                          position: 'absolute',
                          bottom: '5px',
                          left: '5px',
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          #{index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success" disabled={submitting}>
                  {submitting ? (
                    <><FaSync className="spinning" /> {editingOrder ? 'Ukladám...' : 'Vytváram...'}</>
                  ) : (
                    <><FaSave /> {editingOrder ? 'Aktualizovať' : 'Vytvoriť'}</>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingOrder(null);
                    setUploadedImages([]);
                    setExistingImages([]);
                    setFormData({
                      customer_name: '',
                      phone_number: '',
                      delivery_date: '',
                      description: '',
                      image_description: '',
                      status: 'new',
                      source: 'obchod'
                    });
                  }}
                >
                  <FaTimes /> Zrušiť
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Orders */}
      {activeOrders.length > 0 && (
        <>
          <h2 style={{
            marginTop: '30px',
            marginBottom: '20px',
            color: 'var(--text-primary, #e2e8f0)',
            fontSize: '1.25rem',
            fontWeight: '600'
          }}>
            Aktívne objednávky ({activeOrders.length})
          </h2>
          <div className="orders-grid">
            {activeOrders.map(order => (
          <motion.div
            key={order.id}
            className="order-card order-card-clickable"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            onClick={() => setSelectedOrder(order)}
            style={{ cursor: 'pointer' }}
          >
            <div className="order-header">
              <div>
                <div className="order-number">#{order.order_number}</div>
                <div 
                  className="order-source"
                  style={{ backgroundColor: '#95e1d3', opacity: 0.9 }}
                >
                  <FaStore /> Obchod
                </div>
              </div>
              <div 
                className="order-status"
                style={{ backgroundColor: getStatusColor(order.status) }}
              >
                {getStatusText(order.status)}
              </div>
            </div>
            
            <div className="order-body">
              <h3>{order.customer_name}</h3>
              {order.phone_number && (
                <p className="order-phone">
                  <FaPhone /> {order.phone_number}
                </p>
              )}
              {order.delivery_date && (
                <p className="order-delivery">
                  <FaCalendarAlt /> Dodanie: {order.delivery_date}
                </p>
              )}
              {order.description && (
                <div className="order-description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(order.description) }} />
              )}
              {(order.image_ids?.length > 0 || order.image_path) && (
                <OrderGallery orderId={order.id} imageIds={order.image_ids} />
              )}
            </div>
            
            <div className="order-footer" onClick={(e) => e.stopPropagation()}>
              <div className="order-date">
                Vytvorené: {new Date(order.created_at).toLocaleDateString('sk-SK')}
              </div>
              <div className="order-actions">
                <button
                  className="btn-icon edit"
                  onClick={() => startEdit(order)}
                  title="Upraviť"
                >
                  <FaEdit />
                </button>
                <button
                  className="btn-icon delete"
                  onClick={() => handleDelete(order.id)}
                  title="Zmazať"
                >
                  <FaTrash />
                </button>
              </div>
            </div>

            {/* Quick Status Change Buttons */}
            <div className="quick-status-buttons" onClick={(e) => e.stopPropagation()}>
              {order.status !== 'completed' && (
                <button
                  className="quick-status-btn complete"
                  onClick={() => handleStatusChange(order.id, 'completed')}
                  title="Označiť ako dokončenú"
                >
                  <FaCheck /> Dokončiť
                </button>
              )}
              {order.status === 'new' && (
                <button
                  className="quick-status-btn progress"
                  onClick={() => handleStatusChange(order.id, 'in_progress')}
                  title="Označiť ako rozpracovanú"
                >
                  Rozpracovať
                </button>
              )}
            </div>
          </motion.div>
        ))}
          </div>
        </>
      )}

      {/* Divider */}
      {/* Completed Orders — collapsible */}
      {completedOrders.length > 0 && (
        <>
          <button
            type="button"
            className="completed-toggle"
            onClick={() => setShowCompleted(v => !v)}
          >
            {showCompleted ? <FaChevronUp /> : <FaChevronDown />}
            <span>Dokončené objednávky ({completedOrders.length})</span>
            <span className="completed-toggle-hint">
              {showCompleted ? 'skryť' : 'zobraziť'}
            </span>
          </button>

          <AnimatePresence>
            {showCompleted && (
              <motion.div
                className="orders-grid"
                style={{ opacity: 0.85 }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 0.85, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
              >
            {completedOrders.map(order => (
              <motion.div
                key={order.id}
                className="order-card order-card-clickable"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedOrder(order)}
                style={{ cursor: 'pointer' }}
              >
                <div className="order-header">
                  <div>
                    <div className="order-number">#{order.order_number}</div>
                    <div
                      className="order-source"
                      style={{ backgroundColor: '#95e1d3', opacity: 0.9 }}
                    >
                      <FaStore /> Obchod
                    </div>
                  </div>
                  <div
                    className="order-status"
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {getStatusText(order.status)}
                  </div>
                </div>

                <div className="order-body">
                  <h3>{order.customer_name}</h3>
                  {order.phone_number && (
                    <p className="order-phone">
                      <FaPhone /> {order.phone_number}
                    </p>
                  )}
                  {order.delivery_date && (
                    <p className="order-delivery">
                      <FaCalendarAlt /> Dodanie: {order.delivery_date}
                    </p>
                  )}
                  {order.description && (
                    <div className="order-description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(order.description) }} />
                  )}
                  {(order.image_ids?.length > 0 || order.image_path) && (
                    <OrderGallery orderId={order.id} imageIds={order.image_ids} />
                  )}
                </div>

                <div className="order-footer" onClick={(e) => e.stopPropagation()}>
                  <div className="order-date">
                    Vytvorené: {new Date(order.created_at).toLocaleDateString('sk-SK')}
                  </div>
                  <div className="order-actions">
                    <button
                      className="btn-icon edit"
                      onClick={() => startEdit(order)}
                      title="Upraviť"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => handleDelete(order.id)}
                      title="Zmazať"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {filteredOrders.length === 0 && !loading && (
        <div className="no-orders">
          <FaStore />
          <p>Žiadne objednávky z obchodu</p>
        </div>
      )}

      {/* Image Lightbox with gallery navigation */}
      {lightbox && (
        <Lightbox
          orderId={lightbox.orderId}
          ids={lightbox.ids}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onNav={(dir) => setLightbox(lb => ({
            ...lb,
            index: (lb.index + dir + lb.ids.length) % lb.ids.length
          }))}
        />
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="order-modal" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detail objednávky #{selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <strong>Zákazník:</strong> {selectedOrder.customer_name}
              </div>
              {selectedOrder.phone_number && (
                <div className="detail-row">
                  <strong>Telefón:</strong> {selectedOrder.phone_number}
                </div>
              )}
              {selectedOrder.delivery_date && (
                <div className="detail-row">
                  <strong>Dodanie:</strong> {selectedOrder.delivery_date}
                </div>
              )}
              <div className="detail-row">
                <strong>Stav:</strong> 
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
                >
                  {getStatusText(selectedOrder.status)}
                </span>
              </div>
              {selectedOrder.description && (
                <div className="detail-row">
                  <strong>Popis:</strong>
                  <div
                    className="order-description-formatted"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedOrder.description) }}
                  />
                </div>
              )}
              {(selectedOrder.image_ids?.length > 0 || selectedOrder.image_path) && (
                <div className="detail-row full-width">
                  <strong>Obrázky:</strong>
                  <OrderGallery orderId={selectedOrder.id} imageIds={selectedOrder.image_ids} />
                  {selectedOrder.image_description && (
                    <p className="image-description">{selectedOrder.image_description}</p>
                  )}
                </div>
              )}
              <div className="detail-row">
                <strong>Vytvorené:</strong> {new Date(selectedOrder.created_at).toLocaleString('sk-SK')}
              </div>
              {selectedOrder.updated_at && (
                <div className="detail-row">
                  <strong>Naposledy upravené:</strong> {new Date(selectedOrder.updated_at).toLocaleString('sk-SK')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Editor Modal */}
      {showImageEditor && (
        <div className="image-editor-modal">
          <div className="editor-container">
            <div className="editor-header">
              <h3>Upraviť obrázok</h3>
              <button className="close-btn" onClick={() => setShowImageEditor(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="editor-toolbar">
              <button
                className={`tool-btn ${drawingTool === 'pen' ? 'active' : ''}`}
                onClick={() => setDrawingTool('pen')}
              >
                <FaPaintBrush /> Pero
              </button>
              <button
                className={`tool-btn ${drawingTool === 'eraser' ? 'active' : ''}`}
                onClick={() => setDrawingTool('eraser')}
              >
                <FaEraser /> Guma
              </button>
              
              <input
                type="color"
                value={drawingColor}
                onChange={(e) => setDrawingColor(e.target.value)}
              />
              
              <label>
                Hrúbka:
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={lineWidth}
                  onChange={(e) => setLineWidth(e.target.value)}
                />
              </label>
              
              <button className="tool-btn" onClick={handleUndo} disabled={historyStep <= 0}>
                <FaUndo /> Späť
              </button>
              <button className="tool-btn" onClick={handleRedo} disabled={historyStep >= drawingHistory.length - 1}>
                <FaRedo /> Vpred
              </button>
            </div>
            
            <div className="canvas-container">
              <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                className="edit-canvas"
              />
            </div>
            
            <div className="editor-actions">
              <button className="btn btn-success" onClick={saveEditedImage}>
                <FaCheck /> Uložiť zmeny
              </button>
              <button className="btn btn-secondary" onClick={() => setShowImageEditor(false)}>
                <FaTimes /> Zrušiť
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading">Načítavam objednávky z obchodu...</div>
      )}
    </div>
  );
};

export default ShopOrders;